import {
  type ComponentProps,
  type KeyboardEventHandler,
  type PropsWithChildren,
  useEffect,
  useRef,
} from 'react';
import { cn } from '#pie/utils/TailwindUtils';
import type { ButtonProps } from '../button/Button';
import { Button } from '../button/Button';
import type { IconProps } from '../icon/Icon';

export interface InputUploadButtonProps
  extends PropsWithChildren,
    Omit<ComponentProps<'input'>, 'onChange' | 'size'> {
  className?: string;
  variant?: 'tertiarySubtle' | 'tertiarySubtleIcon' | 'errorSubtle';
  size?: ButtonProps['size'];
  icon: IconProps['icon'];
  disabled?: boolean;
  multiple?: boolean;
  acceptedFiles?: string;
  onChange: (value: File[]) => void;
  onDialogOpenChange?: (isOpen: boolean) => void;
}

export function InputUploadButton({
  className,
  variant,
  icon,
  children,
  disabled,
  multiple,
  size,
  acceptedFiles,
  onChange,
  onDialogOpenChange,
  ...props
}: InputUploadButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown: KeyboardEventHandler<HTMLLabelElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.currentTarget.value = '';
    onDialogOpenChange?.(true);
  };

  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) return;

    const closeDialog = () => onDialogOpenChange?.(false);
    input.addEventListener('cancel', closeDialog);
    return () => input.removeEventListener('cancel', closeDialog);
  }, [onDialogOpenChange]);

  return (
    <Button
      as="label"
      className={cn('relative cursor-pointer px-3', className)}
      iconStart={icon}
      isDisabled={disabled}
      onKeyDown={handleKeyDown}
      role="button"
      size={size ?? 'lg'}
      tabIndex={disabled ? -1 : 0}
      variant={variant}
    >
      {/* istanbul ignore next -- @preserve */}
      <input
        accept={acceptedFiles}
        className="absolute inset-0 z-[-1] opacity-0"
        disabled={disabled}
        multiple={multiple}
        onClick={handleInputClick}
        onChange={(event) => {
          onDialogOpenChange?.(false);
          onChange([...(event.target.files || [])]);
        }}
        ref={fileInputRef}
        tabIndex={-1}
        type="file"
        {...props}
      />
      {children}
    </Button>
  );
}
